/**
 * Normalizes a single translation key segment to a style-agnostic canonical form
 * by marking word boundaries with a dash, so that dash-case, snake_case and
 * camelCase variants all map to the same value:
 *
 *   'event-type' -> 'event-type'
 *   'event_type' -> 'event-type'
 *   'eventType'  -> 'event-type'
 *
 * Word boundaries are preserved (rather than collapsed away) so an unseparated,
 * caseless key keeps its own canonical form and is NOT conflated with a
 * multi-word key:
 *
 *   'eventtype'  -> 'eventtype'   (stays distinct from 'event-type')
 */
export function normalizeKeySegment(segment: string): string {
    return segment
        // Insert a dash at camelCase / PascalCase boundaries: 'eventType' -> 'event-Type'.
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        // Treat underscores as word boundaries too: 'event_type' -> 'event-type'.
        .replace(/_/g, '-')
        .toLowerCase();
}

/**
 * Resolves a (possibly mis-styled) dotted translation key to the key path that
 * actually exists in the given translations object, comparing each segment in a
 * style-agnostic way (dash-case / snake_case / camelCase are treated as equal).
 *
 * Behaviour:
 *  - If the exact key already resolves, it is returned unchanged (stock behaviour
 *    and performance are preserved for correctly written keys).
 *  - If a style-agnostic match is found for every segment, the corrected key path
 *    is returned (e.g. `...magaya-event-mappings.event_type` ->
 *    `...magaya-event-mappings.eventType` when the dictionary uses camelCase).
 *  - If no match is found, the original key is returned unchanged, so the stock
 *    missing-translation handling keeps working.
 *
 * The translations object is never mutated, so object-shaped translations (e.g.
 * the PrimeNG config under `common.primeng`, which is read by exact property
 * name) keep their original keys.
 */
export function resolveTranslationKey(
    translations: unknown,
    key: string
): string {
    if (!translations || typeof translations !== 'object' || !key) {
        return key;
    }

    const segments = key.split('.');
    let node: unknown = translations;
    const resolved: string[] = [];

    for (const segment of segments) {
        if (!node || typeof node !== 'object') {
            // Can't descend any further - leave the original key untouched.
            return key;
        }

        const dict = node as Record<string, unknown>;

        // Prefer an exact match to preserve stock behaviour and performance.
        if (Object.prototype.hasOwnProperty.call(dict, segment)) {
            resolved.push(segment);
            node = dict[segment];
            continue;
        }

        // Fall back to a style-agnostic match against the available keys.
        const target = normalizeKeySegment(segment);
        const match = Object.keys(dict).find(
            candidate => normalizeKeySegment(candidate) === target
        );

        if (match === undefined) {
            // No tolerant match - keep the original key so missing-key handling runs.
            return key;
        }

        resolved.push(match);
        node = dict[match];
    }

    return resolved.join('.');
}
